import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Typography, Box, Paper, Stack, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, BackArrowStyle, BoxStyle } from "services/constants";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { FilterSection } from "pages/FilterContext/FilterRow";
import { EmployeeBankTable } from "./EmployeeBankTable";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { EmployeeBankDetailsHeader } from "pages/PayRoll/PayRollHeaders";
import { ArrowBackIos } from "@mui/icons-material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { useSaveView } from "components/CustomComponents/useSaveView";

export const EmployeeBankDetails = ({ setShowReport }) => {
  const {
    domain,
    token,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);

  const [companyId, setCompanyId] = useState(0);
  const [paySheduleDetails, setPayScheduleDetails] = useState(true);
  const [getPayScheduleDetails, setGetPayScheduleDetails] = useState([]);
  const [payScheduleDetailsApi, setPayScheduleDetailsApi] = useState(false);
  const [payScheduleName, setPayScheduleName] = useState("");
  const [listViewPage, setListViewPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [empBankData, setEmpBankData] = useState([]);
  const {
    result: payShedule,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    companyId !== 0 && companyId
      ? `${domain}payschedule-report/?company=${companyId}&company_id=${topbarCompanyId}`
      : null,
  );
  const [salaryDetails, setSalaryDetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const payrollReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  const subModuleId = payrollReports?.submodules?.find(
    (sub) => sub.sub_module_name === "Employee Bank Details",
  )?.sub_module_id;

  // Read-only report -- filter/sort/download/save-view are view-level actions,
  // not edit actions. Gate on viewer permission (see EPFReport/index.js for
  // the fuller reasoning); editor is always false for Reports submodules in
  // the live payload and was blocking these actions for every user.
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Employee Bank Details",
  );

  const handleSavedViewChange = useCallback(() => {
    setListViewPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "employee_bank_saved_views",
    fullHeaders: EmployeeBankDetailsHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    if (topbarCompanyId) {
      setCompanyId(topbarCompanyId);
      reCallApi(true);
      setTimeout(function () {
        setPayScheduleDetails(true);
      }, 100);
    }
  }, [reCallApi, topbarCompanyId]);

  const handlePaySchedule = (event, data) => {
    if (data) {
      setGetPayScheduleDetails(data);
      setPayScheduleName(data.name);
      setPayScheduleDetailsApi(true);
    }
  };

  useEffect(() => {
    if (payShedule.length !== 0 && paySheduleDetails && !loading) {
      setGetPayScheduleDetails(payShedule[0] ? payShedule[0] : []);
      setPayScheduleName(payShedule[0] ? payShedule[0]?.name : "");
      setPayScheduleDetails(false);
      if (payShedule[0]) {
        setPayScheduleDetailsApi(true);
      }
    } else if (payShedule.length === 0) {
      setGetPayScheduleDetails([]);
      setPayScheduleName("");
      setSalaryDetails([]);
    }
  }, [loading, payShedule, paySheduleDetails]);

  useEffect(() => {
    const getSalaryStatementReport = async () => {
      setLoader(true);
      const res = await customGetAPI(
        `${domain}user/bank/payrun-report/?pay_schedule_id=${getPayScheduleDetails?.id}&company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setSalaryDetails(res?.responseDetails);
        setLoader(false);
      } else {
        setLoader(false);
      }
    };
    if (payScheduleDetailsApi) {
      getSalaryStatementReport();
      setPayScheduleDetailsApi(false);
    }
  }, [domain, getPayScheduleDetails, payScheduleDetailsApi, token, topbarCompanyId]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setEmpBankData(filteredData);
      if (listViewPage * rowsPerPage >= filteredData.length) setListViewPage(0);
    },
    [listViewPage, rowsPerPage],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Pay Schedule Name": payScheduleName || "N/A",
      "Report Generated On": new Date().toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    }),
    [payScheduleName, topbarCompanyName],
  );

  return (
    <ThemeProvider theme={theme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={BoxStyle}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <IconButton
              onClick={() => setShowReport(false)}
              sx={BackArrowStyle}
            >
              <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
            </IconButton>

            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
              >
                Employees' Bank Details
              </Typography>

              <Typography variant="body2" color="text.secondary">
                View employee bank details and related information for payroll
                processing and reporting.
              </Typography>
            </Box>
          </Stack>
          <FilterSection
            filterLabel="Filters"
            filters={[
              {
                width: 480,
                props: {
                  dataList: payShedule.map((obj) => ({
                    ...obj,
                    label: obj.name,
                  })),
                  selectedValue: payScheduleName,
                  updateDataFunction: handlePaySchedule,
                  Icons: PersonOutlineIcon,
                  staticLabel: "PaySchedule",
                },
              },
            ]}
          />

          <Paper sx={{ width: "100%", height: "77vh", mt: 2 }}>
            <CustomFilter
              title="Employees' Bank Details"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={salaryDetails}
              filteredData={empBankData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              newButtonComponent={newButtonComponent}
            />

            <Mui.Grid sx={{ ml: 2, mr: 2 }}>
              {loader ? (
                <Loader />
              ) : (
                <EmployeeBankTable
                  dataLength={salaryDetails}
                  salaryDetails={empBankData}
                  listViewPage={listViewPage}
                  setListViewPage={setListViewPage}
                  loader={loader}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  headers={visibleHeaders}
                />
              )}
            </Mui.Grid>
          </Paper>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
